import React from 'react';

import { DeleteOutline, ErrorOutline, HourglassTop, Update } from '@mui/icons-material';
import { DataGrid, GridCellParams } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { DEFAULT_PAGE_SIZES } from '~/consts/filters';

import { PromoMeta } from '~/services/PromoService';

import { BulkOperation, BulkOperationRowStatus } from '../PromoBulkOperations';

import getColumns, { getRefinedColumns } from './columns';

interface Props {
  expirePromo: (id: string) => Promise<void>;
  onPageChange: (page: number, pageSize: number) => void;
  onClick: (rowPromo: GridCellParams<App.PromoData>) => void;
  page: number;
  promos: Array<App.PromoData>;
  sizePerPage: number;
  total: number;
  isLoading: boolean;
  promoMeta: PromoMeta;
  isDev: boolean;
  bulkOperation?: BulkOperation | null;
  bulkRowStatuses?: Record<string, BulkOperationRowStatus>;
  refined?: boolean;
  hideParentPromoId?: boolean;
}

function PromoPage({
  promos,
  total,
  page,
  onPageChange,
  onClick,
  expirePromo,
  sizePerPage,
  isLoading,
  promoMeta,
  isDev,
  bulkOperation = null,
  bulkRowStatuses = {},
  refined = false,
  hideParentPromoId = false,
}: Props) {
  const data = promos.map((promo) => ({
    ...promo,
    actions: { expirePromo },
  }));

  return (
    <DataGrid
      columns={[
        ...(refined ? getRefinedColumns(promoMeta) : getColumns(promoMeta)),
        ...(bulkOperation
          ? [
              {
                field: 'bulk_operation_status',
                headerName: 'Bulk operation',
                sortable: false,
                width: 150,
                renderCell: (params) => {
                  const status = bulkRowStatuses[params.row.id_promo_code];
                  if (status === 'pending') {
                    return <HourglassTop color="warning" titleAccess="Pending" />;
                  }
                  if (status === 'deleted') {
                    return <DeleteOutline color="success" titleAccess="Deleted" />;
                  }
                  if (status === 'updated') {
                    return <Update color="success" titleAccess="Updated" />;
                  }
                  if (status === 'failed') {
                    return <ErrorOutline color="error" titleAccess="Failed" />;
                  }
                  return null;
                },
              },
            ]
          : []),
      ]}
      rows={data}
      rowCount={total}
      pagination
      paginationMode="server"
      paginationModel={{ page: page - 1, pageSize: sizePerPage }}
      slots={{ pagination: GridPagination }}
      pageSizeOptions={DEFAULT_PAGE_SIZES}
      onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
      loading={isLoading}
      getRowId={(row: App.PromoData) => row.id_promo_code}
      density={refined ? 'compact' : 'standard'}
      rowHeight={refined ? 40 : undefined}
      getRowHeight={refined ? undefined : () => 'auto'}
      sx={{ '.MuiDataGrid-cell': { alignItems: refined ? 'center' : 'start' } }}
      onCellClick={(params: GridCellParams<App.PromoData>) => onClick(params)}
      columnVisibilityModel={{
        popup_message_presented_example: isDev,
        cloned_from_promo_id: !hideParentPromoId,
      }}
      disableRowSelectionOnClick
      disableColumnMenu
      autoHeight
    />
  );
}

export default PromoPage;
